// Shared aggregation math for the `#s05` "By category" and "By merchant"
// views. Pure, no data fetching and no fixture reference here (the
// MOCKDATA branch lives in each hook itself -- see useSpendByCategory.ts /
// useSpendByMerchant.ts -- so this module never appears in the fixture
// isolation scan or the fixture eslint boundary).
//
// Binding rule (execution-plan-equivalent, per the 2026-09-06 handoff):
// a CONFIRMED identity event (`has_live_link`) never counts as spend; an
// UNCONFIRMED transfer/cc-payment candidate still counts, exactly as
// `FeedDayGroup.dayTotal` treats it -- `isCountedTransaction` is the single
// shared implementation of that rule.
import type { LineItem, TransactionRow } from '@/api/hooks/useTransactions';
import type { LinkCandidate } from '@/api/hooks/useLinkCandidates';
import { decimalAbs, decimalIsNegative, decimalIsZero, decimalSubtract, decimalSum } from './decimal';

/** A row counts toward any total (spend or otherwise) unless it was removed
 * by the bank (superseded) or is a CONFIRMED identity link. */
export function isCountedTransaction(row: TransactionRow): boolean {
  return row.superseded_at === null && !row.has_live_link;
}

export interface SpendDescriptor {
  key: string;
  label: string;
  /** Merchant view only: the raw bank descriptor, one disclosure away. */
  raw?: string;
  substituted?: boolean;
}

export interface SpendBucket extends SpendDescriptor {
  /** Positive-magnitude decimal string at scale 4 -- render through AmountText. */
  total: string;
  /** 0..1 share of the grand total across all buckets in this result. */
  share: number;
  count: number;
}

export type SpendLineItemDescriptor = (row: TransactionRow, item: LineItem) => SpendDescriptor | null;
export type SpendRemainderDescriptor = (row: TransactionRow) => SpendDescriptor | null;

/** "Travel.ground.parking" -> "Travel › Ground › Parking", the same path
 * shape CategoryChip already renders in the feed. */
export function titleCaseFromSlug(slug: string): string {
  return slug
    .split('.')
    .map((segment) => (segment.length > 0 ? segment[0].toUpperCase() + segment.slice(1) : segment))
    .join(' › ');
}

/**
 * Buckets counted, money-OUT (spend) transactions by a caller-supplied
 * descriptor. Income (amount >= 0) never contributes to a spend ranking.
 * Amounts are summed as exact decimal strings at scale 4 (never floats);
 * an empty result is a legitimate honest state ("no spend in range"), not
 * an error.
 */
export function aggregateSpend(
  rows: TransactionRow[],
  describe: (row: TransactionRow) => SpendDescriptor,
  describeLineItem?: SpendLineItemDescriptor,
  describeRemainder?: SpendRemainderDescriptor,
): SpendBucket[] {
  const buckets = new Map<string, { descriptor: SpendDescriptor; amounts: string[]; rowIds: Set<string> }>();
  for (const row of rows) {
    if (!isCountedTransaction(row)) continue;
    if (Number(row.amount) >= 0) continue;
    const contributions: { descriptor: SpendDescriptor; amount: string }[] = describeLineItem && row.line_items.length > 0
      ? (() => {
          const allocated = decimalSum(row.line_items.map((item) => item.amount));
          const remainder = decimalSubtract(row.amount, allocated);
          if (row.line_items.some((item) => !decimalIsZero(item.amount) && !decimalIsNegative(item.amount)) || (!decimalIsZero(remainder) && !decimalIsNegative(remainder))) {
            throw new Error('Cannot aggregate spend: line-item allocation is overallocated or mixed-sign.');
          }
          return row.line_items
            .map((item) => ({ descriptor: describeLineItem(row, item), amount: item.amount }))
            .filter((contribution): contribution is { descriptor: SpendDescriptor; amount: string } => contribution.descriptor !== null);
        })()
      : [{ descriptor: describe(row), amount: row.amount }];
    if (describeLineItem && describeRemainder && row.line_items.length > 0) {
      const allocated = decimalSum(row.line_items.map((item) => item.amount));
      const remainder = decimalSubtract(row.amount, allocated);
      if (!decimalIsZero(remainder)) {
        const descriptor = describeRemainder(row);
        if (descriptor) contributions.push({ descriptor, amount: remainder });
      }
    }
    for (const { descriptor, amount } of contributions) {
      const existing = buckets.get(descriptor.key);
      if (existing) {
        existing.amounts.push(amount);
        existing.rowIds.add(row.transaction_id);
      } else {
        buckets.set(descriptor.key, { descriptor, amounts: [amount], rowIds: new Set([row.transaction_id]) });
      }
    }
  }
  const totals = Array.from(buckets.values()).map(({ descriptor, amounts, rowIds }) => ({
    ...descriptor,
    total: decimalAbs(decimalSum(amounts)),
    count: rowIds.size,
  }));
  const grandTotal = totals.reduce((sum, t) => sum + Number(t.total), 0);
  return totals
    .map((t) => ({ ...t, share: grandTotal > 0 ? Number(t.total) / grandTotal : 0 }))
    .sort((a, b) => Number(b.total) - Number(a.total));
}

/** Grand total across buckets, as an exact decimal string -- feeds the
 * composition bar and the page header total. Returns null for an empty
 * result so callers show the honest "no spend" state rather than "$0.00". */
export function spendGrandTotal(buckets: SpendBucket[]): string | null {
  if (buckets.length === 0) return null;
  return decimalSum(buckets.map((b) => b.total));
}

/**
 * Magnitude of spend that is really a pending transfer leg.
 *
 * An UNCONFIRMED transfer candidate still counts as spend -- that is the
 * binding rule, and these aggregations honour it. But in the feed the user
 * also sees a "Likely transfer?" review card next to the row, whereas a
 * ranked total gives no such context: an internal transfer can sit at the top
 * of "spend" looking like real money spent. Counting it silently would be
 * accurate and still misleading.
 *
 * This returns the magnitude the caller should disclose alongside the total,
 * so the number stays truthful AND explains itself. Only the negative leg is
 * counted, because only that leg reaches the spend aggregation.
 * Returns null when there is nothing pending -- never "0.00".
 */
export function pendingTransferSpendTotal(candidates: LinkCandidate[]): string | null {
  const amounts: string[] = [];
  for (const candidate of candidates) {
    for (const leg of candidate.legs) {
      if (!isCountedTransaction(leg)) continue;
      if (Number(leg.amount) >= 0) continue;
      amounts.push(leg.amount);
    }
  }
  if (amounts.length === 0) return null;
  const total = decimalAbs(decimalSum(amounts));
  return decimalIsZero(total) ? null : total;
}
